/**
 * AlertsByHourDowHeatmap — 7×24 cell grid showing when alerts fire.
 *
 * One of the two widgets most useful for scheduling (the other being
 * Alerts by Date). "We always get hit between 2am and 4am on Saturdays"
 * pops out here when no line chart would surface it.
 *
 * Color ramp is a single-hue dark→accent scale; we intentionally avoid
 * rainbow gradients because perceptual ordering breaks down.
 */
import { useMemo, useState } from 'react';
import clsx from 'clsx';
import type { HourDowMatrix } from '../../services/reportsApi';

const DOW_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function colorFor(value: number, max: number): string {
  if (max <= 0 || value <= 0) return 'bg-navy-800';
  const ratio = value / max;
  // 5 brackets keeps the palette chunky enough to read; continuous opacity
  // produces indistinguishable cells under low counts.
  if (ratio > 0.8) return 'bg-accent';
  if (ratio > 0.6) return 'bg-accent/80';
  if (ratio > 0.4) return 'bg-accent/60';
  if (ratio > 0.2) return 'bg-accent/40';
  return 'bg-accent/20';
}

interface Props {
  matrix: HourDowMatrix;
}

interface Hover {
  dow: number;
  hour: number;
  value: number;
}

export default function AlertsByHourDowHeatmap({ matrix }: Props) {
  const [hover, setHover] = useState<Hover | null>(null);

  const max = useMemo(() => {
    let m = 0;
    for (const row of matrix) for (const v of row) if (v > m) m = v;
    return m;
  }, [matrix]);

  const total = useMemo(() => {
    let sum = 0;
    for (const row of matrix) for (const v of row) sum += v;
    return sum;
  }, [matrix]);

  return (
    <div className="glass-card p-5">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-sm font-semibold text-white">Alerts by Hour / Day</h3>
          <p className="text-xs text-gray-500">
            {hover
              ? `${DOW_LABELS[hover.dow]} · ${String(hover.hour).padStart(2, '0')}:00 — ${hover.value.toLocaleString()} alerts`
              : `${total.toLocaleString()} alerts · peak ${max.toLocaleString()}`}
          </p>
        </div>
      </div>

      {/* Hour header */}
      <div className="overflow-x-auto">
        <div className="inline-block min-w-full">
          <div className="flex gap-0.5 ml-10 mb-1">
            {Array.from({ length: 24 }).map((_, h) => (
              <div
                key={h}
                className="w-6 text-[10px] text-gray-500 text-center"
              >
                {h % 3 === 0 ? h : ''}
              </div>
            ))}
          </div>

          {/* Rows: Sun..Sat */}
          {matrix.map((row, dow) => (
            <div key={dow} className="flex items-center gap-0.5 mb-0.5">
              <div className="w-10 text-[11px] text-gray-500 text-right pr-2">
                {DOW_LABELS[dow]}
              </div>
              {row.map((value, hour) => (
                <div
                  key={hour}
                  onMouseEnter={() => setHover({ dow, hour, value })}
                  onMouseLeave={() => setHover(null)}
                  className={clsx(
                    'w-6 h-5 rounded-sm cursor-default transition-all',
                    colorFor(value, max),
                    hover?.dow === dow && hover?.hour === hour && 'ring-1 ring-accent ring-offset-1 ring-offset-navy-900',
                  )}
                  title={`${DOW_LABELS[dow]} ${String(hour).padStart(2, '0')}:00 — ${value} alerts`}
                />
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-2 mt-4 text-[11px] text-gray-500">
        <span>Less</span>
        <div className="flex gap-0.5">
          <div className="w-4 h-3 rounded-sm bg-navy-800" />
          <div className="w-4 h-3 rounded-sm bg-accent/20" />
          <div className="w-4 h-3 rounded-sm bg-accent/40" />
          <div className="w-4 h-3 rounded-sm bg-accent/60" />
          <div className="w-4 h-3 rounded-sm bg-accent/80" />
          <div className="w-4 h-3 rounded-sm bg-accent" />
        </div>
        <span>More</span>
      </div>
    </div>
  );
}
